import { Action } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fetchCustomerByIdQueryOptions } from "@/api/customers";
import { fetchCustomerEnrollmentsQueryOptions } from "@/api/enrollments";
import { Card, CardContent } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { CustomerEnrollmentList } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerEnrollmentList";

// Search params schema
const enrollmentSearchSchema = z.object({
	page: z.number().min(1).catch(1),
	limit: z.number().min(1).max(100).catch(10),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/enrollments",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, customerId } = params;

		// Preload customer data
		context.queryClient.ensureQueryData(
			fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
		);

		// Preload customer enrollments data
		context.queryClient.ensureQueryData(
			fetchCustomerEnrollmentsQueryOptions(userId, token, orgId, customerId, {
				page: 1,
				limit: 10,
			}),
		);

		return { userId, token, orgId, customerId };
	},
	validateSearch: enrollmentSearchSchema,
	component: CustomerEnrollmentsPage,
});

function CustomerEnrollmentsPage() {
	const { t } = useTranslation();
	const { orgId, customerId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const search = Route.useSearch();

	// Customer data is preloaded by the route loader; read it from the cache to
	// prefill the create-enrollment form.
	const { data: customer } = useSuspenseQuery(
		fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
	);

	// Local state for pagination
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});

	// Authorization check
	const can = useCan();
	const canManageEnrollments = can(Action.MANAGE_ENROLLMENTS);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	};

	const handlePaginationMetaChange = useCallback(
		(meta: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		}) => {
			setPaginationMeta(meta);
		},
		[],
	);

	return (
		<div className="h-full flex flex-col space-y-6">
			<Card className="flex-1 flex flex-col overflow-hidden my-3 py-3">
				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-3">
					{/* Toolbar: create enrollment prefilled with this customer */}
					{canManageEnrollments && paginationMeta.total > 0 && (
						<div className="shrink-0 flex justify-end">
							<Link
								to="/app/org/$orgId/enrollments/new"
								params={{ orgId }}
								search={{
									customerId: customer.id,
									customerFirstName: customer.firstName,
									customerLastName: customer.lastName,
									...(customer.email && { customerEmail: customer.email }),
									...(customer.idType && { customerIdType: customer.idType }),
									...(customer.idNumber && {
										customerIdNumber: customer.idNumber,
									}),
								}}
								className="btn btn-primary btn-sm"
								data-testid="customer-enrollments-add-button"
							>
								<Plus className="w-4 h-4" />
								{t("enrollments.actions.create")}
							</Link>
						</div>
					)}

					{/* Table (desktop) / card list (mobile) */}
					<div className="flex-1 overflow-hidden">
						<CustomerEnrollmentList
							organizationId={orgId}
							customerId={customerId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							userId={userId}
							token={token}
							canManageEnrollments={canManageEnrollments || false}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>

					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
